import React, { useState, useRef, useEffect } from "react";

interface SafeImageProps {
  src: string;
  alt: string;
  className: string;
  fallbackComponent?: React.ReactNode;
}

const SafeImage: React.FC<SafeImageProps> = ({
  src,
  alt,
  className,
  fallbackComponent,
}) => {
  const [imageStatus, setImageStatus] = useState<
    "loading" | "loaded" | "error"
  >("loading");
  const [currentSrc, setCurrentSrc] = useState(src);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setCurrentSrc(src);
    setImageStatus("loading");
  }, [src]);

  const handleImageLoad = () => {
    setImageStatus("loaded");
  };

  const handleImageError = () => {
    console.log("Image failed to load:", currentSrc);
    setImageStatus("error");

    // Try alternative methods
    if (currentSrc === src) {
      // First fallback: try with no-referrer
      const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(
        src
      )}`;
      setCurrentSrc(proxyUrl);
      setImageStatus("loading");
    }
  };

  if (imageStatus === "error") {
    return (
      fallbackComponent || (
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex-shrink-0 ring-2 ring-gray-100 flex items-center justify-center">
          <span className="text-xs text-gray-500">No Image</span>
        </div>
      )
    );
  }

  return (
    <>
      {imageStatus === "loading" && (
        <div className="size-7 2xl:w-10 2xl:h-10 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:400%_100%] animate-shimmer flex-shrink-0 ring-2 ring-gray-100 relative overflow-hidden">
          <svg
            className="w-4 h-4 text-gray-400 absolute inset-0 m-auto animate-pulse"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4S8 5.79 8 8s1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
            />
          </svg>
        </div>
      )}
      <img
        ref={imgRef}
        src={currentSrc}
        alt={alt}
        className={`${className} ${
          imageStatus === "loaded" ? "block" : "hidden"
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
        loading="lazy"
      />
    </>
  );
};

export default SafeImage;
